import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";
import { useNavigation } from "../../utils/navigation";

const Hero = styled.div`
  width: 100%;
  background-color: var(--text-100);
  color: var(--bg-100);
  padding-bottom: var(--spacing-xxl);
  display: flex;
  justify-content: center;
`;

const HeroInner = styled.div`
  width: calc(100% - var(--spacing-xl) * 2);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

const Title = styled.h1`
  font-size: var(--font-size-xxlarge);
  margin: 0 0 var(--spacing-sm) 0;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const Subtitle = styled.p`
  margin: 0 0 var(--spacing-xl) 0;
  max-width: 480px;
  color: var(--bg-300);
  font-size: var(--font-size-base);
`;

const SearchBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px var(--spacing-md) 12px 40px;
  font-size: 14px;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  background-color: transparent;
  color: var(--bg-100);

  &::placeholder {
    color: var(--bg-300);
  }

  &:focus {
    outline: none;
    border-bottom-color: var(--primary-300);
  }
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const Container = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const MaxWidthWrapper = styled.div`
  margin: var(--spacing-xl) 0 var(--spacing-xxl) 0;
  width: calc(100% - var(--spacing-xl) * 2);
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

const ResultCount = styled.p`
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const LetterGroup = styled.div`
  display: flex;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg) 0;
  border-bottom: 1px solid var(--bg-300);

  &:first-of-type {
    padding-top: 0;
  }
`;

const LetterHeading = styled.span`
  flex-shrink: 0;
  width: 48px;
  font-size: var(--font-size-xlarge);
  font-weight: 700;
  color: var(--primary-200);
`;

const NameList = styled.div`
  flex: 1;
  columns: 2;
  column-gap: var(--spacing-lg);

  @media (min-width: 768px) {
    columns: 3;
  }
  @media (min-width: 1025px) {
    columns: 4;
  }
`;

const BrandName = styled.p`
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-base);
  color: var(--text-100);
  cursor: pointer;
  break-inside: avoid;
  width: fit-content;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--primary-200);
    text-decoration: underline;
  }
`;

const EmptyState = styled.p`
  color: var(--text-200);
`;

const BrandsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { categories } = useContext(ProductContext);
  const { goToCategory } = useNavigation();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!categories) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? categories.filter((c) => c.name.toLowerCase().includes(q))
      : categories;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, query]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((category) => {
      const first = category.name[0]?.toUpperCase() || "#";
      const letter = /[A-Z]/.test(first) ? first : "#";
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(category);
    });
    return Object.keys(groups)
      .sort()
      .map((letter) => ({ letter, items: groups[letter] }));
  }, [filtered]);

  return (
    <Container>
      <Hero className="header-top-padding">
        <HeroInner>
          <Title>{t("BRANDS.TITLE", "All Brands")}</Title>
          <Subtitle>
            {t(
              "BRANDS.SUBTITLE",
              "Browse every nicotine pouch brand available in our store."
            )}
          </Subtitle>
          <SearchBox>
            <SearchIcon width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                stroke="var(--bg-300)"
                strokeWidth="2"
              />
              <path
                d="M21 21L16.65 16.65"
                stroke="var(--bg-300)"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder={t("BRANDS.SEARCH", "Search brands...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </SearchBox>
        </HeroInner>
      </Hero>

      <MaxWidthWrapper>
        {categories && categories.length > 0 ? (
          <>
            <ResultCount>
              {filtered.length} {t("BRANDS.RESULTS", "brands")}
            </ResultCount>
            {grouped.map((group) => (
              <LetterGroup key={group.letter}>
                <LetterHeading>{group.letter}</LetterHeading>
                <NameList>
                  {group.items.map((category) => (
                    <BrandName
                      key={category.id}
                      onClick={() => navigate(goToCategory(category.name))}
                    >
                      {category.name}
                    </BrandName>
                  ))}
                </NameList>
              </LetterGroup>
            ))}
            {filtered.length === 0 && (
              <EmptyState>
                {t("BRANDS.NO_RESULTS", "No brands match your search.")}
              </EmptyState>
            )}
          </>
        ) : (
          <EmptyState>{t("BRANDS.EMPTY", "No brands available.")}</EmptyState>
        )}
      </MaxWidthWrapper>
    </Container>
  );
};

export default BrandsMain;
